// Представление «Сводка» раздела «Расчёт»: сколько стоит месяц и где недобор.
//
// Два вопроса, ради которых экран существует. Первый — «сколько мы платим в этом месяце»: он
// сегодня отвечается сложением ведомостей глазами. Второй — «где не хватает людей»: недобор
// это и деньги, которых компания не получит по договору, и люди, которых надо искать сегодня.
//
// Проценты и итоги приходят с сервера уже посчитанными: делить их здесь значило бы завести
// второй источник тех же чисел.

import {
  RiBuilding4Line,
  RiCalculatorLine,
  RiHandCoinLine,
  RiScales3Line,
  RiShieldCheckLine,
} from '@remixicon/react';

import { useLocation, useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import { formatHours } from '@/shared/hours/format';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { cn } from '@/utils/cn';
import { countLabel } from '@/shared/text/plural';
import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { ExportButton } from '@/shared/reports/ExportButton';
import { PrintButton } from '@/shared/reports/PrintButton';
import { Block } from '@/shared/ui/block';
import { ДоляОтЦелого } from '@/shared/reports/ДоляОтЦелого';
import { СтоимостьМесяца } from '@/shared/reports/СтоимостьМесяца';
import { СтруктураСуммы } from '@/shared/reports/СтруктураСуммы';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';
import { useCustomersQuery, useLegalEntitiesQuery, useSitesQuery } from '@/shared/org/queries';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';

import type { ReportScope, SitePayroll, StaffingOut } from '@/shared/api/endpoints/reports';
import {
  exportDebts,
  exportPayoutRegister,
  exportPayrollSummary,
  exportStaffing,
} from '@/shared/api/endpoints/reports';
import type { LossCut } from '@/shared/api/endpoints/losses';
import { buildPath } from '@/shared/routing/paths';
import { buildSearch } from '@/shared/routing/search';
import { useФокус } from '@/shared/ui/block/фокус';
import { useПереносДолгов, пустоУВсех, состояниеПереноса } from '@/shared/debts/перенос';
import {
  formatPeriodKey,
  formatPeriodTitle,
  parsePeriod,
  shiftPeriod,
  type Period,
} from '@/shared/time/period';
import type { SearchState } from '@/shared/routing/search';

import {
  useDebtsReport,
  useLossesReport,
  usePayoutRegister,
  usePayrollSummary,
  usePayrollTrend,
  useStaffing,
} from '../queries';

// Плитка и запрос панели живут в разделе «Деньги» — там они и появились, там же и остаются
// стартовым экраном финансового директора. Здесь берётся ровно тот же источник, а не второй:
// два места, считающие одно и то же по-разному, у заказчика уже были, и разъезжались они
// молча. Ссылка через границу раздела — цена одного источника, и она честнее копии.
import { KpiTile } from '@/modules/money/components/KpiTile';
import { useDashboard } from '@/modules/money/queries';
import {
  FILTER_CUSTOMER,
  FILTER_FOCUS,
  FILTER_LEGAL_ENTITY,
  FILTER_ONLY_OPEN,
  FILTER_PERIOD,
  FILTER_SITE,
  FOCUS_STAFFING,
  ONLY_OPEN,
  VIEW_DEBTS,
  VIEW_SHEETS,
  VIEW_SUMMARY,
} from '../route';

// Своей плитки у сводки больше нет: величина в продукте одна (`shared/ui/metric`). Здешняя
// отличалась от соседних рамкой и кеглем, и на одном экране с карточкой подотчётного лица это
// читалось как два разных продукта.

/**
 * Насколько итог расходится с цепочкой вычетов. Плюс — к выплате больше, чем даёт вычитание.
 *
 * Ноль здесь норма, но не единственный законный ответ. У заказчика в перенесённых ведомостях
 * есть колонки, которых нет в этой четвёрке: премии и доплаты идут в его формуле со знаком
 * плюс (`=SUM(F5-G5-…-L5+M5)`), а часть строк не сходится и у него самого. Замер на боевом
 * контуре 26.08.2026: 45 967,78 ₽ на трёх ведомостях из семидесяти.
 */
export function расхождениеЦепочки(данные: {
  total_accrued: number;
  total_official: number;
  total_advance_withheld: number;
  total_deductions: number;
  total_payout: number;
}): number {
  const поЦепочке =
    данные.total_accrued -
    данные.total_official -
    данные.total_advance_withheld -
    данные.total_deductions;
  return данные.total_payout - поЦепочке;
}

/** «Должно минус вышло»; старый сервер поля не несёт — берём прежнюю сумму недоборов. */
function недоборЧасов(данные: StaffingOut): string | null {
  return данные.total_net_hours ?? данные.total_deficit_hours ?? null;
}

/**
 * Подпись под недобором: арифметика, которая сходится, и то, что она прячет.
 *
 * «Вышло X из Y» сходится с «должно минус вышло» вычитанием. Если переработка на одних объектах
 * покрывает недобор на других, это надо сказать отдельно: объект, где пост не закрыт, от чужой
 * переработки не становится закрытым.
 */
function недоборНаОбъектах(данные: StaffingOut): string {
  const основа = `вышло ${formatHours(данные.total_fact_hours ?? '0')} из ${formatHours(данные.total_norm_hours ?? '0')}`;
  const разница = Number(данные.total_net_hours ?? 'NaN');
  const наОбъектах = Number(данные.total_deficit_hours ?? 'NaN');
  if (!Number.isFinite(разница) || !Number.isFinite(наОбъектах)) {
    return основа;
  }
  if (наОбъектах > 0 && наОбъектах > разница) {
    return `${основа}; на объектах с недобором не закрыто ${formatHours(данные.total_deficit_hours!)} ч, переработка на других это покрывает`;
  }
  // Недобора нет нигде, есть только переработка: число под подписью «недобор» отрицательное,
  // как и на первом экране, и минус надо назвать словом.
  return разница < 0 ? `${основа}: минус — это переработка` : основа;
}

/**
 * Строка разреза укомплектованности: сколько МЕСТ по постам закрыто.
 *
 * Проход по боевым данным 23.09.2026: разрез по заказчикам писал «0 из 0» (постов не заведено)
 * и «18 из 1» (назначено восемнадцать на одно место) — оба читаются как поломка. Закрытые места
 * — это требуемые без недобора: люди сверх постов на одном объекте не закрывают недобор на
 * другом, поэтому они названы отдельно, а не сложены в «из».
 */
export function местаПоПостам(группа: {
  required: number;
  assigned: number;
  deficit: number;
}): string {
  if (группа.required === 0) {
    return группа.assigned > 0 ? `постов нет · назначено ${группа.assigned}` : 'постов нет';
  }
  const закрыто = группа.required - группа.deficit;
  const хвост = [
    группа.deficit > 0 ? `не хватает ${группа.deficit}` : null,
    группа.assigned > закрыто ? `сверх постов ${группа.assigned - закрыто}` : null,
  ].filter((часть) => часть !== null);
  return [`${закрыто} из ${группа.required}`, ...хвост].join(' · ');
}

/** У скольких людей ведомости суммы неизвестны. Старый ответ сервера поля не несёт — ноль. */
function неизвестноВСтроке(строка: SitePayroll): number {
  return строка.unknown_employees ?? 0;
}

/**
 * Сумма ведомости неизвестна: её не считали вовсе либо не знают денег ни у кого из людей.
 *
 * У ПетроАльянса 23.09.2026 стоял ноль за 1 152 отработанных часа: ведомость «считалась», но
 * тарифа не было ни у кого, и ноль читался как «объект ничего не стоит».
 */
function суммаНеизвестна(строка: SitePayroll): boolean {
  return (
    строка.calculated === false ||
    (строка.employees > 0 && неизвестноВСтроке(строка) >= строка.employees)
  );
}

/**
 * Разрез потерь: короткий список «кто — сколько».
 *
 * Не общий компонент: он про этот один отчёт, и вынести его в общий слой значило бы обещать,
 * что он подойдёт следующему разрезу, — а следующий разрез (по объектам) сюда не подойдёт по
 * существу, а не по вёрстке.
 */
function РазрезПотерь({
  заголовок,
  строки,
  пусто,
}: {
  заголовок: string;
  строки: readonly LossCut[];
  пусто: string;
}) {
  return (
    <div className='flex flex-col gap-1 border-t border-stroke-soft-200 px-5 py-3'>
      <span className='text-label-sm text-text-strong-950'>{заголовок}</span>
      {строки.length === 0 ? (
        <span className='text-paragraph-xs text-text-sub-600'>{пусто}</span>
      ) : (
        <ul className='flex flex-col gap-0.5'>
          {строки.slice(0, ПОТОЛОК_РАЗРЕЗА).map((строка) => (
            <li
              key={строка.key ?? строка.title}
              className='flex items-baseline justify-between gap-3 text-paragraph-sm text-text-sub-600'
            >
              <span className='truncate'>{строка.title}</span>
              <span className='tabular-nums shrink-0 text-text-strong-950'>
                {formatKopecks(строка.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {строки.length > ПОТОЛОК_РАЗРЕЗА && (
        <span className='text-paragraph-xs text-text-sub-600'>
          Показаны первые {ПОТОЛОК_РАЗРЕЗА} из{' '}
          {countLabel(строки.length, ['строки', 'строк', 'строк'])}.
        </span>
      )}
    </div>
  );
}

//: Сколько строк разреза показывать. Больше — это уже отчёт внутри отчёта: сводку читают
//: сверху вниз целиком, и двадцать фамилий подряд в ней перестают читать вместе с остальным.
const ПОТОЛОК_РАЗРЕЗА = 5;

export function SummaryScreen({ search }: { search: SearchState }) {
  // Куда смотреть. На сводку ведут разные плитки пульта — долги, недобор, к выплате, — а
  // экран один и длинный: без этого переход по «Подробно» приводил человека в начало
  // страницы, где ответа на его вопрос нет. Подсветка однократная и гаснет сама: постоянная
  // рамка означала бы, что с блоком что-то не так.
  const фокусУкомплектованности = useФокус(search.filters[FILTER_FOCUS] === FOCUS_STAFFING);
  // Переход с этого же экрана несёт СВОИ отборы: месяц и юрлицо, которые человек здесь и
  // выбрал. Уехать без них значило бы показать ему другие числа, чем те, по которым он нажал.
  const разрезЗдесь = (view: string, filters: Record<string, string> = {}): string =>
    buildPath({ section: 'payroll', view }) +
    buildSearch({ ...search, panels: [], filters: { ...search.filters, ...filters } });
  const navigate = useNavigate();
  const location = useLocation();
  const период = parsePeriod(search.filters[FILTER_PERIOD]);

  // Сквозные отборы экрана (тз, раздел 5.7). Живут в адресе, а не в памяти вкладки: ссылка на
  // «сводку по объекту Заря за май» обязана открываться у коллеги тем же срезом.
  const отбор: ReportScope = {
    legal_entity_id: search.filters[FILTER_LEGAL_ENTITY],
    customer_id: search.filters[FILTER_CUSTOMER],
    site_id: search.filters[FILTER_SITE],
  };
  // Права на пять блоков РАЗНЫЕ, а вкладка одна. Она открывается по праву на панели
  // показателей (`core.dashboard.view`), которое есть и у табельщика, и у старшего объекта, —
  // а ведомости, реестр выплат и потери им закрыты. До 06.09.2026 экран посылал все пять
  // запросов независимо от прав, и эти двое видели ТРИ КРАСНЫХ БЛОКА ИЗ ПЯТИ: «не удалось
  // загрузить сводку по ведомостям», «…реестр выплат», «…невозвратные потери». Красная
  // ошибка читается как поломка программы — человек идёт спрашивать, почему сломалось, а
  // сломано ничего не было: данные просто не его.
  //
  // Блок без права не рисуется и запрос не уходит. Это удобство навигации, а не защита:
  // изоляция живёт на сервере выборкой (правило 12), и открытый через адресную строку запрос
  // всё так же вернёт отказ.
  const права = useAuthStore((state) => state.permissions);
  const виднаВедомость = can(права, 'core.payroll.view');
  const виднаУкомплектованность = can(права, 'core.dashboard.view');
  const видныДолги = can(права, 'core.debt.view');
  const видныПотери = can(права, 'core.loss.view');
  // Три отбора ходят в справочники организации, а их право есть не у всех, кому открыта эта
  // вкладка: у старшего объекта его нет вовсе. Три выпадающих списка с надписью «не удалось
  // загрузить» над экраном, который в остальном работает, — та же ложная поломка, что и
  // красные блоки. Своя выборка у него и так одна, отбирать ему не из чего.
  const видносправочники = can(права, 'core.dictionary.view');

  const юрлица = useLegalEntitiesQuery(видносправочники);
  const заказчики = useCustomersQuery({}, видносправочники);
  // Объекты сужаются выбранным заказчиком: список из шестидесяти четырёх, из которых человеку
  // нужны три, — это тот же список, только длиннее.
  const объекты = useSitesQuery({ active: true, customer_id: отбор.customer_id }, видносправочники);

  // Четыре крупных числа — первое, что тз называет в разделе 5.7: «Стартовый экран (крупные
  // числа): четыре показателя за период с фильтром по юридическому лицу». До 06.09.2026 они
  // жили ТОЛЬКО в правой колонке раздела «Деньги», то есть за правом на подотчёт, и трое из
  // тех, кому тз даёт панель показателей, не видели их ни на одном экране. Здесь у них есть
  // то, чего нет там: календарь и отбор по юрлицу — сквозные отборы, которых тз и требует.
  const панель = useDashboard(
    { legal_entity_id: отбор.legal_entity_id, year: период.year, month: период.month },
    виднаУкомплектованность || виднаВедомость || видныДолги,
  );

  const сводка = usePayrollSummary(период.year, период.month, отбор, виднаВедомость);
  // Ряд за двенадцать месяцев. Отдельным запросом от сводки: сводка отвечает «сколько стоит
  // ЭТОТ месяц», ряд — «растёт или падает», и живут они в кэше по-разному.
  const ряд = usePayrollTrend(период.year, период.month, отбор, виднаВедомость);
  // Месяц уходит в запрос: тз 5.7 требует брать факт укомплектованности ИЗ ТАБЕЛЯ, а табель
  // всегда за конкретный период. Без месяца отчёт считал бы только головы на сегодня — и
  // именно так он и работал до 06.09.2026, при том что экран показывает выбранный месяц.
  const укомплектованность = useStaffing(
    { ...отбор, year: период.year, month: период.month },
    виднаУкомплектованность,
  );
  const реестрВыплат = usePayoutRegister(период.year, период.month, отбор, виднаВедомость);
  // Долги и потери отбор по заказчику и объекту НЕ принимают: долг висит на человеке, а не на
  // объекте, и «долги по объекту Заря» означало бы что-то своё у каждого читателя. Юрлицо им
  // применимо — из его ведомости и удерживают.
  const долги = useDebtsReport(период.year, период.month, отбор.legal_entity_id, видныДолги);
  // Пустой разрез долгов до переноса остатков — не «никто не должен». См. `shared/debts/перенос`.
  const переносДолгов = состояниеПереноса(useПереносДолгов(видныДолги));
  const пустоДолгов = пустоУВсех(переносДолгов);
  const потери = useLossesReport(период.year, период.month, видныПотери);

  /** Поставить или снять один отбор, не трогая остальные и не унося панели. */
  function сменитьОтбор(ключ: string, значение: string | null) {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    void navigate({
      pathname: location.pathname,
      search: buildSearch({ ...search, filters: фильтры, panels: [] }),
    });
  }

  function сменитьМесяц(следующий: Period) {
    void navigate({
      pathname: location.pathname,
      search: buildSearch({
        ...search,
        filters: { ...search.filters, [FILTER_PERIOD]: formatPeriodKey(следующий) },
        panels: [],
      }),
    });
  }

  return (
    <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
      {/* Календарь тот же, что в трёх соседних представлениях. Без него сводка была
          единственным представлением раздела, где месяц НЕ НАЗВАН и не переключается: четыре
          блока говорили «за этот месяц», «Сколько стоит месяц», и какой это месяц, человек
          вспоминал по соседней вкладке. Числа на этом экране — деньги за период, и период
          обязан стоять рядом с ними. */}
      {/* Пометки «скрыть» здесь нет намеренно: правило печати прячет всё, что документом не
          является, и отдельная пометка на отборах ничего не держала бы — проверено порчей. */}
      <Toolbar>
        <PeriodStepper
          label='Месяц'
          title={formatPeriodTitle(период)}
          onPrev={() => сменитьМесяц(shiftPeriod(период, -1))}
          onNext={() => сменитьМесяц(shiftPeriod(период, 1))}
        />
        {видносправочники && (
          <div className='min-w-56'>
            <SelectField
              id='summary-entity'
              label='Юрлицо'
              value={отбор.legal_entity_id ?? NO_VALUE}
              options={(юрлица.data?.items ?? []).map((строка) => ({
                value: строка.id,
                label: строка.short_name ?? строка.name,
              }))}
              noneLabel='все юрлица'
              placeholder='все юрлица'
              pending={юрлица.isPending}
              failed={юрлица.isError}
              failedError={юрлица.error as ApiError | null}
              onChange={(значение) =>
                сменитьОтбор(FILTER_LEGAL_ENTITY, значение === NO_VALUE ? null : значение)
              }
            />
          </div>
        )}
        {видносправочники && (
          <div className='min-w-56'>
            <SelectField
              id='summary-customer'
              label='Заказчик'
              value={отбор.customer_id ?? NO_VALUE}
              options={(заказчики.data?.items ?? []).map((строка) => ({
                value: строка.id,
                label: строка.name,
              }))}
              noneLabel='все заказчики'
              placeholder='все заказчики'
              pending={заказчики.isPending}
              failed={заказчики.isError}
              failedError={заказчики.error as ApiError | null}
              onChange={(значение) =>
                сменитьОтбор(FILTER_CUSTOMER, значение === NO_VALUE ? null : значение)
              }
            />
          </div>
        )}
        {видносправочники && (
          <div className='min-w-56'>
            <SelectField
              id='summary-site'
              label='Объект'
              value={отбор.site_id ?? NO_VALUE}
              options={(объекты.data?.items ?? []).map((строка) => ({
                value: строка.id,
                label: строка.name,
              }))}
              noneLabel='все объекты'
              placeholder='все объекты'
              pending={объекты.isPending}
              failed={объекты.isError}
              failedError={объекты.error as ApiError | null}
              onChange={(значение) =>
                сменитьОтбор(FILTER_SITE, значение === NO_VALUE ? null : значение)
              }
            />
          </div>
        )}
        {/* Печать отчётов обещана тз (раздел 5.7) в одном ряду с выгрузкой в Excel, и до
            06.09.2026 её не было ни у одного отчёта, кроме ведомости. Разница не в удобстве:
            акт, сводку и укомплектованность подписывают и подшивают, а Excel для этого надо
            сначала открыть на машине, где он есть. */}
        <PrintButton />
      </Toolbar>
      {/* Документ — сами блоки отчёта. Всё, что не помечено, при печати прячется: иначе на
          лист уходит окно целиком, с полосой разделов и панелями. */}
      <main
        data-печать='документ'
        className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-bg-weak-50 p-5'
      >
        {панель.data !== undefined && (
          <div className='grid grid-cols-2 gap-4 xl:grid-cols-4'>
            {/* Одна и та же карточка обязана вести себя одинаково на обоих экранах. До
                21.09.2026 здесь у трёх плиток из четырёх адреса не было вовсе: на пульте
                «Подробно» показывалось, здесь — нет, и человек, запомнивший, что в число
                можно ткнуть, тыкал в него впустую. */}
            <KpiTile
              title='Неподтверждённый подотчёт'
              value={панель.data.unconfirmed_total}
              href='/money/accountable'
            />
            <KpiTile
              title='Долги сотрудников'
              value={панель.data.debts_total}
              href={разрезЗдесь(VIEW_DEBTS, { [FILTER_ONLY_OPEN]: ONLY_OPEN })}
            />
            <KpiTile
              title='Объектов с недобором'
              value={панель.data.understaffed_sites}
              href={разрезЗдесь(VIEW_SUMMARY, { [FILTER_FOCUS]: FOCUS_STAFFING })}
            />
            <KpiTile
              title='К выплате за период'
              value={панель.data.payable_total}
              // Месяц сводки передаётся ЯВНО. «Ведомости» без месяца в адресе открываются на
              // расчётном (прошлом), а сводка — на текущем: без этого плитка про один месяц вела
              // бы в ведомости другого, и число «к выплате» не находило бы себя на экране.
              href={разрезЗдесь(VIEW_SHEETS, { [FILTER_PERIOD]: formatPeriodKey(период) })}
            />
          </div>
        )}

        {виднаВедомость && (
          <Block
            flush
            icon={RiCalculatorLine}
            title='Сколько стоит месяц'
            note={(() => {
              if (сводка.data === undefined) return undefined;
              const срез = parseMoment(сводка.data.generated_at);
              return срез === null ? undefined : formatDataAsOf(срез);
            })()}
            action={
              <ExportButton
                что='расчёт'
                собрать={() =>
                  exportPayrollSummary({ year: период.year, month: период.month, ...отбор })
                }
              />
            }
          >
            <ScreenState
              data={сводка.data}
              error={сводка.error ?? null}
              subject='сводку по ведомостям'
              isEmpty={(данные) => данные.items.length === 0}
              onRetry={() => void сводка.refetch()}
              loading={<Skeleton className='m-5 h-24' />}
              empty={
                <div className='p-5'>
                  <EmptyState
                    icon={RiCalculatorLine}
                    title='За этот месяц ведомостей нет'
                    description='Ведомость заводится на каждый объект: откройте «Ведомости» и нажмите «Создать ведомость».'
                  />
                </div>
              }
            >
              {(данные) => {
                // Юрлицо в подписи каждой строки — только когда их в списке БОЛЬШЕ ОДНОГО. У
                // заказчика оно одно, и на шестидесяти четырёх строках подряд стояло одно и то
                // же «ООО ЧОП «Барк»»: шестьдесят четыре повтора, которые ничего не различают.
                const однаКомпания =
                  new Set(данные.items.map((строка) => строка.legal_entity_id)).size <= 1;
                return (
                  <>
                    {/* Двенадцать месяцев подряд. Стоит ПЕРВЫМ в блоке, до ряда величин за
                      выбранный месяц: вопрос «растёт ли» шире вопроса «сколько сейчас», и
                      человек, открывший экран, отвечает на них именно в этом порядке.

                      Ряда может не быть — за прошлые месяцы ведомостей могло не быть вовсе.
                      Тогда блок начинается с величин, как и раньше: пустой график на месте
                      данных читается как поломка. */}
                    {ряд.data !== undefined && ряд.data.items.length > 0 && (
                      <div className='border-b border-stroke-soft-200 p-5'>
                        <СтоимостьМесяца data={ряд.data} showHeadline={false} />
                      </div>
                    )}
                    {/* Порядок и состав — та же цепочка, что в итогах ведомости: начислено минус
                    три вычета равно к выплате. Раньше здесь стояли те же четыре величины, но
                    результатом вперёд и без удержаний, и на живых данных они не сходились —
                    198 064,51 при нулевой официальной части и нулевом авансе давали 194 064,51.
                    Ряд чисел в строку читается как формула, и несошедшаяся формула означает для
                    человека не «показано не всё», а «посчитано неверно». */}
                    <MetricRow columns={4} className='p-5'>
                      <Metric label='Начислено' value={formatKopecks(данные.total_accrued)} />
                      <Metric
                        label='− Официальная часть'
                        value={formatKopecks(данные.total_official)}
                        note='вычитается до удержаний'
                        tone={данные.total_official === 0 ? 'приглушённый' : 'обычный'}
                      />
                      <Metric
                        label='− Аванс и удержания'
                        value={formatKopecks(
                          данные.total_advance_withheld + данные.total_deductions,
                        )}
                        note='аванс, форма, билеты, обучение, штрафы'
                        tone={
                          данные.total_advance_withheld + данные.total_deductions === 0
                            ? 'приглушённый'
                            : 'обычный'
                        }
                      />
                      <Metric
                        label='= К выплате'
                        value={formatKopecks(данные.total_payout)}
                        // «366 человек» под суммой, посчитанной по семнадцати, читается как «за
                        // 366 человек месяц стоит столько» (проход по боевым данным 23.09.2026).
                        note={
                          (данные.total_unknown_employees ?? 0) > 0
                            ? `посчитано ${данные.total_employees - (данные.total_unknown_employees ?? 0)} из ${данные.total_employees}: сумма неполная`
                            : countLabel(данные.total_employees, ['человек', 'человека', 'человек'])
                        }
                        tone='важный'
                      />
                    </MetricRow>
                    {/* Сверка показывается ВСЕГДА, когда не сходится, — как в полосе итогов
                      ведомости. На перенесённых книгах заказчика это не редкость: у него самого
                      «Итого» строки расходится с суммой её колонок (протокол переноса называет
                      четыре такие строки на −32 275,00 и одну на 6 966,94). Молчать нельзя:
                      человек видит ряд чисел, читает его как формулу и, не найдя сходимости,
                      перестаёт верить всему экрану. Названная разница переводит это из «программа
                      считает неверно» в «вот столько принесли данные, разберите». */}
                    {расхождениеЦепочки(данные) !== 0 && (
                      <p className='px-5 pb-3 text-paragraph-xs text-warning-base'>
                        Цепочка не сходится на {formatKopecks(Math.abs(расхождениеЦепочки(данные)))}{' '}
                        ₽: к выплате {расхождениеЦепочки(данные) > 0 ? 'больше' : 'меньше'}, чем
                        даёт вычитание. Так бывает на перенесённых ведомостях: в них есть колонки,
                        которых нет в этой четвёрке, — премии и доплаты сверх начисленного. Разбор
                        по каждой ведомости — в её панели проверки.
                      </p>
                    )}
                    <ul className='flex flex-col divide-y divide-stroke-soft-200 border-t border-stroke-soft-200'>
                      {данные.items.map((строка) => (
                        <li
                          key={строка.payroll_id}
                          className='flex items-center justify-between gap-4 px-5 py-2.5'
                        >
                          <div className='flex min-w-0 flex-col'>
                            <span className='truncate text-paragraph-sm text-text-strong-950'>
                              {строка.site_name ?? 'Административная ведомость'}
                            </span>
                            <span className='text-paragraph-xs text-text-sub-600'>
                              {однаКомпания ? null : <>{строка.legal_entity_name} · </>}
                              {countLabel(строка.employees, [
                                'человек',
                                'человека',
                                'человек',
                              ])} · {formatHours(строка.hours_fact)} ч
                              {неизвестноВСтроке(строка) > 0 &&
                                неизвестноВСтроке(строка) < строка.employees && (
                                  <> · без суммы у {неизвестноВСтроке(строка)}</>
                                )}
                            </span>
                          </div>
                          {/* Ноль по НЕПОСЧИТАННОЙ ведомости — это «не знаем», а не «объект
                            стоит ноль». На стенде так выглядели 63 объекта из 64, и сводка
                            «сколько стоит месяц» утверждала, что месяц почти ничего не стоит.
                            Прочерк разводит эти два факта, а причина названа один раз под
                            списком. */}
                          <span
                            className={cn(
                              'shrink-0 tabular-nums text-label-sm',
                              суммаНеизвестна(строка)
                                ? 'text-text-sub-600'
                                : 'text-text-strong-950',
                            )}
                          >
                            {суммаНеизвестна(строка) ? '—' : formatKopecks(строка.total_payout)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {данные.items.some(суммаНеизвестна) && (
                      // Причина называется ОДИН раз под списком, а не по разу в каждой строке.
                      // До 23.09.2026 здесь было «ещё не считали — нажмите «Пересчитать»», и это
                      // было неправдой для ведомостей, где пересчёт уже был, а денег нет из-за
                      // пустых тарифов: пересчёт там не поможет.
                      <p className='px-5 py-2 text-paragraph-xs text-text-sub-600'>
                        Прочерк — это «не знаем», а не ноль: по{' '}
                        {данные.items.filter(суммаНеизвестна).length} из {данные.items.length}{' '}
                        {данные.items.length === 1 ? 'ведомости' : 'ведомостей'} суммы неизвестны.
                        Либо расчёта ещё не было, либо у людей не заполнен тариф или нет часов — что
                        именно, написано в полосе проверки самой ведомости.
                      </p>
                    )}
                  </>
                );
              }}
            </ScreenState>
          </Block>
        )}

        {виднаУкомплектованность && (
          <Block
            flush
            ref={фокусУкомплектованности.ссылка}
            className={фокусУкомплектованности.className}
            icon={RiBuilding4Line}
            title='Где не хватает людей'
            action={<ExportButton что='недобор' собрать={() => exportStaffing(отбор)} />}
          >
            <ScreenState
              data={укомплектованность.data}
              error={укомплектованность.error ?? null}
              subject='укомплектованность объектов'
              isEmpty={(данные) => данные.items.length === 0}
              onRetry={() => void укомплектованность.refetch()}
              loading={<Skeleton className='m-5 h-24' />}
              empty={
                <div className='p-5'>
                  <EmptyState
                    icon={RiBuilding4Line}
                    title='Объектов пока нет'
                    description='Объекты и посты заводятся в разделе «Объекты»: пока их нет, укомплектовывать нечего.'
                  />
                </div>
              }
            >
              {(данные) => {
                const недобор = данные.items.filter((строка) => строка.deficit > 0);
                // Объект без постов ни закрыт, ни открыт: требовать на нём нечего. До 23.09.2026
                // такие шли в долю закрытыми — «76 из 78 объектов закрыты полностью» и 97%
                // у заказчика, где часть объектов стояла без единого поста.
                const сПостами = данные.items.filter((строка) => строка.required > 0).length;
                const безПостов = данные.items.length - сПостами;
                return (
                  <>
                    {/* Ответ блока одним числом: сколько объектов закрыто полностью.
                      «Не хватает шести человек» ответом не является — шесть из ста сорока пяти
                      и шесть из двенадцати — разные новости.

                      Считается по ОБЪЕКТАМ, а не по головам, и это не мелочь. Голов назначено
                      288 при 145 требуемых: перебор на одних объектах не закрывает недобор на
                      других, и доля «288 из 145» дала бы 199 процентов — то есть выглядела бы
                      ошибкой расчёта ровно там, где человек ищет проблему. Ту же ловушку уже
                      обошли в правой колонке «Объектов», и обходить её надо одинаково. */}
                    <div className='border-b border-stroke-soft-200'>
                      <ДоляОтЦелого
                        value={сПостами - данные.understaffed_sites}
                        max={сПостами}
                        подпись={
                          `${сПостами - данные.understaffed_sites} из ${countLabel(сПостами, ['объекта', 'объектов', 'объектов'])} с постами закрыты полностью` +
                          (безПостов > 0
                            ? `; ещё у ${countLabel(безПостов, ['объекта', 'объектов', 'объектов'])} посты не заведены, в доле их нет`
                            : '')
                        }
                        когдаНеизвестно='Посты на объектах не заведены: считать укомплектованность не от чего.'
                      />
                    </div>
                    <MetricRow columns={4} className='p-5'>
                      <Metric
                        label='Не хватает людей'
                        value={String(данные.total_deficit)}
                        note={`на ${countLabel(данные.understaffed_sites, ['объекте', 'объектах', 'объектах'])}`}
                      />
                      <Metric
                        label='Требуется по постам'
                        value={String(данные.total_required)}
                        note={
                          данные.total_assigned > данные.total_required
                            ? `назначено ${данные.total_assigned}: перебор на одних объектах не закрывает недобор на других`
                            : `назначено ${данные.total_assigned}`
                        }
                      />
                      {/* Часы из ТАБЕЛЯ, а не из назначений: тз 5.7 называет табель источником
                        факта. Головы отвечают на «кого искать сегодня», часы — на «сколько
                        компания недополучит по договору»: назначенный человек может не выйти,
                        и до 06.09.2026 отчёт этого не показывал вовсе. */}
                      {/* Число то же, что «Недобор в часах» на первом экране: «должно минус
                        вышло» по табелям, где отмечена хоть одна смена; минус — переработка.
                        До 23.09.2026 здесь стояла сумма недоборов по объектам с пустыми
                        табелями в придачу — 732 часа против 12 на первом экране за тот же
                        сентябрь, и в августе она не сходилась с «вышло X из Y» вычитанием. */}
                      <Metric
                        label='Недобор в часах'
                        value={
                          недоборЧасов(данные) == null
                            ? 'считает табель'
                            : formatHours(недоборЧасов(данные)!)
                        }
                        note={
                          недоборЧасов(данные) == null
                            ? 'табеля с отмеченными сменами за этот месяц ещё нет'
                            : недоборНаОбъектах(данные)
                        }
                      />
                    </MetricRow>

                    {/* Итоги по заказчику: тз 5.7 требует их дословно, и считает их сервер.
                      Сложенное на экране разошлось бы с общим итогом на первом же объекте,
                      скрытом отбором, и человек прочитал бы это как ошибку в расчёте. */}
                    {(данные.by_customer ?? []).length > 1 && (
                      <ul className='flex flex-col divide-y divide-stroke-soft-200 border-t border-stroke-soft-200'>
                        {(данные.by_customer ?? []).map((группа) => (
                          <li
                            key={группа.key}
                            className='flex items-center justify-between gap-4 px-5 py-2'
                          >
                            <span className='truncate text-paragraph-sm text-text-sub-600'>
                              {группа.title}
                            </span>
                            <span className='shrink-0 text-paragraph-sm text-text-sub-600'>
                              {местаПоПостам(группа)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {недобор.length === 0 ? (
                      <p className='rounded-lg bg-bg-white-0 px-5 py-3 text-paragraph-sm text-text-sub-600'>
                        Недобора нет: на всех объектах людей столько, сколько требуют посты.
                      </p>
                    ) : (
                      <ul className='flex flex-col divide-y divide-stroke-soft-200 border-t border-stroke-soft-200'>
                        {недобор.map((строка) => (
                          <li
                            key={строка.site_id}
                            className='flex items-center justify-between gap-4 px-5 py-2.5'
                          >
                            <div className='flex min-w-0 flex-col'>
                              <span className='truncate text-paragraph-sm text-text-strong-950'>
                                {строка.site_name}
                              </span>
                              <span className='text-paragraph-xs text-text-sub-600'>
                                {[строка.customer_name, строка.city].filter(Boolean).join(' · ') ||
                                  строка.legal_entity_name}
                              </span>
                            </div>
                            <div className='flex shrink-0 items-center gap-3'>
                              <span className='text-paragraph-sm text-text-sub-600'>
                                {строка.assigned} из {строка.required}
                              </span>
                              <Badge.Root variant='lighter' color='red' size='medium'>
                                −{строка.deficit}
                              </Badge.Root>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                );
              }}
            </ScreenState>
          </Block>
        )}

        {/* Долги сотрудников (сценарий 59). Блок отвечает на два вопроса сразу: сколько всего
          висит по типам и по кому надо принять решение. Второе важнее: уволенный с долгом —
          это не строка отчёта, а заблокированный расчёт, который сам себя не разблокирует. */}
        {виднаВедомость && (
          <Block
            flush
            icon={RiHandCoinLine}
            title='Кому перевести'
            note={
              реестрВыплат.data === undefined
                ? undefined
                : `${countLabel(реестрВыплат.data.total_employees, ['человек', 'человека', 'человек'])} на ${formatRubles(реестрВыплат.data.total_payout)}`
            }
            action={
              <ExportButton
                что='реестр выплат'
                собрать={() =>
                  exportPayoutRegister({ year: период.year, month: период.month, ...отбор })
                }
              />
            }
          >
            <ScreenState
              data={реестрВыплат.data}
              error={реестрВыплат.error}
              subject='реестр выплат'
              isEmpty={(данные) => данные.items.length === 0}
              onRetry={() => void реестрВыплат.refetch()}
              loading={<Skeleton className='m-5 h-24' />}
              empty={
                <div className='p-5'>
                  <EmptyState
                    title='Переводить пока нечего'
                    description='За этот месяц ни по одной ведомости нет положительной суммы к выплате. Она появится, когда ведомости посчитают.'
                  />
                </div>
              }
            >
              {(данные) => (
                <div className='flex flex-col'>
                  {/* Готовность выплат одним числом: сколько из месячной суммы уже ушло людям.
                    Тот же вид, что у укомплектованности выше, и это намеренно — вопрос один и
                    тот же, «какая доля от целого», только величины разные. */}
                  <div className='border-b border-stroke-soft-200'>
                    <ДоляОтЦелого
                      value={данные.paid_total}
                      max={данные.total_payout}
                      подпись={`переведено ${данные.paid_employees} из ${данные.total_employees}`}
                      когдаНеизвестно='К выплате за месяц ничего не вышло: делить не от чего.'
                    />
                  </div>
                  {/* Крупная величина в ряду ровно одна — ответ на вопрос «сколько переводить
                    сегодня». Остальное объясняет, из чего он сложился. */}
                  <MetricRow columns={3}>
                    <Metric
                      tone='важный'
                      label='Осталось перевести'
                      value={formatKopecks(данные.total_payout - данные.paid_total)}
                      note={countLabel(данные.total_employees - данные.paid_employees, [
                        'человек',
                        'человека',
                        'человек',
                      ])}
                    />
                    <Metric
                      label='Уже переведено'
                      value={formatKopecks(данные.paid_total)}
                      note={countLabel(данные.paid_employees, ['человек', 'человека', 'человек'])}
                    />
                    <Metric
                      label='Всего за месяц'
                      value={formatKopecks(данные.total_payout)}
                      // Людей здесь МЕНЬШЕ, чем в блоке «сколько стоит месяц», и это надо
                      // объяснить прямо: два счётчика при одинаковой сумме на одном экране
                      // читаются как ошибка. В реестр не идут те, у кого к выплате ноль:
                      // строка «перевести 0,00» в банк-клиенте это мусор.
                      note={
                        сводка.data !== undefined &&
                        сводка.data.total_employees > данные.total_employees
                          ? `${countLabel(данные.total_employees, ['человек', 'человека', 'человек'])} из ${сводка.data.total_employees}: с нулём к выплате не переводят`
                          : countLabel(данные.total_employees, ['человек', 'человека', 'человек'])
                      }
                    />
                  </MetricRow>
                  <ul className='flex flex-col divide-y divide-stroke-soft-200 border-t border-stroke-soft-200'>
                    {данные.items.slice(0, 12).map((строка) => (
                      <li
                        key={`${строка.employee_id}:${строка.legal_entity_id}`}
                        className='flex items-center justify-between gap-4 px-5 py-2.5'
                      >
                        <div className='flex min-w-0 flex-col'>
                          <span className='truncate text-paragraph-sm text-text-strong-950'>
                            {строка.employee_name}
                          </span>
                          <span className='text-paragraph-xs text-text-sub-600'>
                            {строка.legal_entity_name}
                            {строка.payout_last4
                              ? ` · •••• ${строка.payout_last4}`
                              : ' · реквизитов нет'}
                          </span>
                        </div>
                        <div className='flex shrink-0 items-center gap-3'>
                          {строка.is_paid && (
                            <Badge.Root variant='lighter' color='green' size='medium'>
                              Выплачено
                            </Badge.Root>
                          )}
                          <span className='tabular-nums text-label-sm text-text-strong-950'>
                            {formatKopecks(строка.total_payout)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {данные.items.length > 12 && (
                    // Список здесь короткий намеренно: экран отвечает «сколько и кому», а
                    // работают с реестром в файле — его и передают в банк-клиент.
                    <p className='px-5 py-2.5 text-paragraph-xs text-text-sub-600'>
                      Показаны первые двенадцать из{' '}
                      {countLabel(данные.items.length, ['строки', 'строк', 'строк'])}. Весь реестр —
                      в выгрузке.
                    </p>
                  )}
                </div>
              )}
            </ScreenState>
          </Block>
        )}

        {видныДолги && (
          <Block
            flush
            icon={RiScales3Line}
            title='Долги сотрудников'
            // Долг висит на ЧЕЛОВЕКЕ, а не на объекте, и отбор по заказчику и объекту к нему не
            // применяется. Молча игнорировать выбранный отбор нельзя: человек прочитает числа
            // как «долги по этому объекту» и уйдёт с неверным ответом.
            note={
              отбор.customer_id !== undefined || отбор.site_id !== undefined
                ? 'Отбор по заказчику и объекту здесь не применяется: долг числится за человеком, а не за объектом'
                : undefined
            }
            action={
              <ExportButton
                что='реестр долгов'
                собрать={() =>
                  exportDebts({
                    year: период.year,
                    month: период.month,
                    legal_entity_id: отбор.legal_entity_id,
                  })
                }
              />
            }
          >
            <ScreenState
              data={долги.data}
              error={долги.error}
              subject='долги сотрудников'
              isEmpty={(данные) => данные.items.length === 0}
              onRetry={() => void долги.refetch()}
              loading={<Skeleton className='m-5 h-24' />}
              empty={
                <div className='p-5'>
                  <EmptyState
                    icon={RiHandCoinLine}
                    title={
                      переносДолгов === 'перенесены' ? 'Действующих долгов нет' : пустоДолгов.title
                    }
                    description={
                      переносДолгов === 'перенесены'
                        ? 'Ни за кем не числится форма, билет, обучение или удостоверение. Появится долг — здесь будет его тип, остаток и сколько удержано за месяц.'
                        : пустоДолгов.description
                    }
                  />
                </div>
              }
            >
              {(данные) => (
                <>
                  <MetricRow columns={4} className='p-5'>
                    <Metric
                      label='Осталось погасить'
                      value={formatKopecks(данные.total_outstanding)}
                      note={countLabel(данные.items.length, [
                        'тип долга',
                        'типа долга',
                        'типов долга',
                      ])}
                    />
                    <Metric label='Удержано за месяц' value={formatKopecks(данные.total_repaid)} />
                    <Metric
                      label='Уволены с долгом'
                      value={String(данные.leaving_with_debt.length)}
                      note={
                        данные.leaving_with_debt.length === 0
                          ? 'решать не по кому'
                          : 'по каждому нужно решение'
                      }
                    />
                  </MetricRow>

                  {/* Полоса вместо плоского списка. Список отвечал «сколько по каждому типу» и
                    молчал про «что тут главное»: семь строк с суммами читаются по одной, и
                    чтобы понять, что половину долга составляет форма, надо было сложить их
                    глазами. Ширина сегмента отвечает на это за долю секунды.

                    Доли сервер по долгам не считает, поэтому колонки «Доля» здесь нет: делить
                    те же суммы на экране значит завести второй ответ на один вопрос. */}
                  <div className='border-t border-stroke-soft-200 p-5'>
                    <СтруктураСуммы
                      // Подпись НЕ повторяет величину над ней: «Осталось погасить 1 915 300,00»
                      // стоит в ряду величин и тут же появлялось ещё раз крупным числом — одно и
                      // то же дважды подряд. Здесь другой вопрос: не «сколько», а «из чего».
                      label='Из чего сложился остаток'
                      total={данные.total_outstanding}
                      nameHeader='Тип долга'
                      noteHeader='Удержано за месяц'
                      rows={данные.items.map((строка) => ({
                        id: строка.debt_type,
                        title: строка.title,
                        subtitle: `${countLabel(строка.people, ['человек', 'человека', 'человек'])}, ${countLabel(строка.debts, ['долг', 'долга', 'долгов'])}`,
                        amount: строка.outstanding_total,
                        // Ноль ЦИФРОЙ, а не фразой. Фраза «не удерживали» верна, но в колонке
                        // она повторяется у каждой строки: на живом экране их оказалось пять
                        // подряд — стена одинаковых слов вместо столбца чисел, который можно
                        // сравнить взглядом. Единицу колонка уже назвала заголовком.
                        note: formatRubles(строка.repaid_in_period),
                      }))}
                    />
                  </div>

                  {данные.leaving_with_debt.length === 0 ? null : (
                    <div className='flex flex-col gap-2 rounded-xl border border-warning-base bg-bg-white-0 px-4 py-3'>
                      <span className='text-label-sm text-text-strong-950'>
                        Уволены, но долг остался
                      </span>
                      <span className='text-paragraph-xs text-text-sub-600'>
                        Расчёт по ним заблокирован, пока по каждому долгу не принято решение:
                        удержать, списать или реструктурировать.
                      </span>
                      <ul className='flex flex-col gap-1'>
                        {данные.leaving_with_debt.map((человек) => (
                          <li
                            key={человек.employee_id}
                            className='flex items-baseline justify-between gap-3'
                          >
                            <span className='text-paragraph-sm text-text-strong-950'>
                              {человек.employee_name}
                            </span>
                            <span className='tabular-nums text-label-sm text-text-strong-950'>
                              {formatKopecks(человек.outstanding)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </ScreenState>
          </Block>
        )}

        {/* Невозвратные потери (сценарий 77). Отвечают на вопрос, который идёт следом за
          «сколько потеряли»: где именно течёт. Если каждый месяц уходят билеты, дело не в
          людях, а в правиле компенсации. */}
        {видныПотери && (
          <Block flush icon={RiShieldCheckLine} title='Невозвратные потери за месяц'>
            <ScreenState
              data={потери.data}
              error={потери.error}
              subject='невозвратные потери'
              isEmpty={(данные) => данные.items.length === 0}
              onRetry={() => void потери.refetch()}
              loading={<Skeleton className='h-20 w-full' />}
              empty={
                <div className='p-5'>
                  <EmptyState
                    icon={RiShieldCheckLine}
                    title='За этот месяц потерь нет'
                    description='Ни одного списанного долга и ни одного невозвращённого остатка. Здесь появится разбивка по билетам, форме, подотчётным средствам и авансам, если что-то признают невозвратным.'
                  />
                </div>
              }
            >
              {(данные) => (
                <>
                  <MetricRow columns={4} className='p-5'>
                    <Metric
                      label='Потеряно за месяц'
                      value={formatKopecks(данные.total_amount)}
                      note={countLabel(данные.total_cases, ['случай', 'случая', 'случаев'])}
                    />
                    {/* Накопленный итог стоит РЯДОМ с месячным, а не вместо него. Заказчик
                      оценивает потери словами «на невозвратных потерях уже миллионы
                      исчисляются» — это про накопленное, и месячная цифра на такой вопрос не
                      отвечает. А одно только накопленное не отвечает на «а в этом месяце». */}
                    {данные.total_all_time !== undefined && (
                      <Metric
                        label='Потеряно за всё время'
                        value={formatKopecks(данные.total_all_time)}
                        note='накопленным итогом'
                      />
                    )}
                  </MetricRow>
                  {/* Самый ценный для руководства блок был нарисован беднее всех: два числа
                    и плоский список из четырёх строк. Заказчик оценивает это словами «на
                    невозвратных потерях уже миллионы исчисляются» — и по списку было не
                    видно, ЧТО именно течёт: билеты, форма или списанный подотчёт. Полоса
                    отвечает на это первым взглядом. */}
                  <div className='border-t border-stroke-soft-200 p-5'>
                    <СтруктураСуммы
                      label='На чём потеряли'
                      total={данные.total_amount}
                      footnote={countLabel(данные.total_cases, ['случай', 'случая', 'случаев'])}
                      nameHeader='Причина'
                      rows={данные.items.map((строка) => ({
                        id: строка.loss_type,
                        title: строка.title,
                        subtitle: `${countLabel(строка.people, ['человек', 'человека', 'человек'])}, ${countLabel(строка.cases, ['случай', 'случая', 'случаев'])}`,
                        amount: строка.amount,
                      }))}
                    />
                  </div>

                  {/* Два разреза, которых тз требует у этого отчёта и которых не было ни одного.
                    Разбивка по типам отвечает на «что течёт» и молчит про «у кого» и «с чьего
                    решения»: второе спрашивают через полгода, и спросить было не с кого.

                    Разреза по объектам здесь нет намеренно — потеря числится за человеком, а
                    не за объектом, и привязка через сегодняшнее назначение отнесла бы майскую
                    потерю туда, где человек стоит сейчас. */}
                  <РазрезПотерь
                    заголовок='У кого потеряли'
                    строки={данные.by_employee ?? []}
                    пусто='Ни за кем: потери этого месяца не привязаны к людям'
                  />
                  <РазрезПотерь
                    заголовок='Кто признал невозвратными'
                    строки={данные.by_decider ?? []}
                    пусто='Решений в этом месяце не принимали'
                  />
                </>
              )}
            </ScreenState>
          </Block>
        )}
      </main>
    </div>
  );
}
